/**
 * useTeamOwnerEmail
 *
 * Fetches the email address of the first active owner in a team.
 * Used by AccessDisabledScreen to offer a direct "Contact owner" mailto link.
 *
 * Only runs when enabled=true (i.e. when isMemberDisabled=true) to avoid
 * unnecessary Firestore reads during normal app usage.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';

export function useTeamOwnerEmail(teamId: string | null, enabled: boolean): string | undefined {
  const [ownerEmail, setOwnerEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !teamId) {
      setOwnerEmail(undefined);
      return;
    }

    const membersRef = collection(db, 'teams', teamId, 'members');
    const q = query(
      membersRef,
      where('role', '==', 'owner'),
      where('active', '!=', false),
      limit(1)
    );

    getDocs(q)
      .then((snap) => {
        const doc = snap.docs[0];
        if (doc) {
          setOwnerEmail((doc.data().email as string) || undefined);
        }
      })
      .catch((err) => {
        logger.warn('[useTeamOwnerEmail] Failed to fetch owner email:', err);
      });
  }, [teamId, enabled]);

  return ownerEmail;
}
